// The KPI file's mapping table (v3 `orpheusFilePreviewPresentation.ts`, KUR-8): the dry run's cells
// grouped back into the file's rows. The backend sends one cell per (row, metric) — the results of
// `scorecards.file_preview_read` — so a wide file's row comes back as several cells sharing a
// `row_index`.
import type { FilePreview, FilePreviewCell } from '../models/orpheusSetup.types'

import type { Translate } from './orpheusSetupCardPresentation'

/** The words and the number format the table reads in — `useFormat`'s, never a fixed locale. */
export interface PreviewLabels {
  t: Translate
  formatNumber: (value: number) => string
}

export type PreviewRowStatus = 'matched' | 'unchecked' | 'needs_choice' | 'excluded'

export interface PreviewRow {
  rowIndex: number
  /** What the file says, as written. */
  fileValue: string
  /** The workspace value it lands on; empty when it lands nowhere yet. */
  mapsTo: string
  candidates: string[]
  targets: Record<string, string>
  status: PreviewRowStatus
  /** Why the row is left out or waits, in words; empty for a matched row. */
  note: string
}

const K = 'orpheus.filePreview'

// The wire strings are a backend contract (`scorecards/services/ingest/skip_reasons.py`).
const SKIP_REASON_KEYS: Record<string, string> = {
  empty_row: 'emptyRow',
  metric_not_in_config: 'metricNotInConfig',
  missing_target_value: 'missingTargetValue',
  invalid_target_value: 'invalidTargetValue',
  missing_breakdown_value: 'missingBreakdownValue',
  invalid_breakdown_value: 'invalidBreakdownValue',
  reserved_axis_value: 'reservedAxisValue',
  duplicate_row: 'duplicateRow',
  missing_sub_breakdown_value: 'missingSubBreakdownValue',
  invalid_sub_breakdown_value: 'invalidSubBreakdownValue',
  reserved_sub_axis_value: 'reservedAxisValue',
  internal_error: 'internalError',
  breakdown_value_needs_choice: 'needsChoice',
  sub_breakdown_value_needs_choice: 'needsChoice',
  non_ad_breakdown_value: 'nonAdValue',
  non_ad_sub_breakdown_value: 'nonAdValue',
}

const ROW_WARNING_KEYS: Record<string, string> = {
  unverified_breakdown_value: 'unverified',
  new_breakdown_value: 'newValue',
  unverified_sub_breakdown_value: 'unverified',
  new_sub_breakdown_value: 'newValue',
}

const NEEDS_CHOICE = ['breakdown_value_needs_choice', 'sub_breakdown_value_needs_choice']
// Written as the file spells it, never matched to a workspace value: a typo gets no data.
const UNVERIFIED = ['unverified_breakdown_value', 'unverified_sub_breakdown_value']

export function skipReasonLabel(t: Translate, reason: string | undefined): string {
  if (!reason) return ''
  const key = SKIP_REASON_KEYS[reason]
  return key ? t(`${K}.skip.${key}`) : reason.replace(/_/g, ' ')
}

function formatTarget(labels: PreviewLabels, value: unknown): string {
  const number = Number(value)
  return value !== '' && Number.isFinite(number) ? labels.formatNumber(number) : String(value)
}

function joinAxes(primary?: string, sub?: string): string {
  return [primary, sub].filter((part) => part && String(part).trim()).join(' · ')
}

function fileValue(cell: FilePreviewCell): string {
  if (cell.status === 'created') {
    return joinAxes(
      cell.file_breakdown_value || cell.breakdown_value,
      cell.file_sub_breakdown_value || cell.sub_breakdown_value,
    )
  }
  return joinAxes(cell.breakdown_value, cell.sub_breakdown_value)
}

// The backend's `detail` is a developer sentence; only a bad target value says something the
// label doesn't (which value it was).
function skipNote(t: Translate, cell: FilePreviewCell | undefined): string {
  const label = skipReasonLabel(t, cell?.reason)
  return cell?.reason === 'invalid_target_value' && cell.detail ? `${label}: ${cell.detail}` : label
}

/** The metric columns, in the order the file first names them. */
export function previewMetrics(preview: FilePreview): string[] {
  return [
    ...new Set(
      preview.cells
        .map((cell) => cell.metric)
        .filter((metric): metric is string => Boolean(metric)),
    ),
  ]
}

export function previewRows(labels: PreviewLabels, preview: FilePreview): PreviewRow[] {
  const { t } = labels
  const byRow = new Map<number, FilePreviewCell[]>()
  for (const cell of preview.cells) {
    byRow.set(cell.row_index, [...(byRow.get(cell.row_index) ?? []), cell])
  }

  return [...byRow.entries()]
    .sort(([a], [b]) => a - b)
    .map(([rowIndex, cells]) => {
      const created = cells.filter((cell) => cell.status === 'created')
      const waiting = cells.find((cell) => NEEDS_CHOICE.includes(cell.reason ?? ''))
      const skipped = cells.find((cell) => cell.status === 'skipped')
      const first = created[0] ?? waiting ?? cells[0]

      const targets: Record<string, string> = {}
      for (const cell of created) {
        if (cell.metric && cell.target_value !== undefined) {
          targets[cell.metric] = formatTarget(labels, cell.target_value)
        }
      }

      const warnings = created.flatMap((cell) => cell.warnings ?? [])
      const unchecked = warnings.some((code) => UNVERIFIED.includes(code))
      let status: PreviewRowStatus = 'matched'
      if (waiting) status = 'needs_choice'
      else if (!created.length) status = 'excluded'
      else if (unchecked) status = 'unchecked'
      const warning = warnings.find((code) => ROW_WARNING_KEYS[code])
      const note =
        created.length && !waiting
          ? warning
            ? t(`${K}.warning.${ROW_WARNING_KEYS[warning]}`)
            : ''
          : skipNote(t, waiting ?? skipped)

      return {
        rowIndex,
        fileValue: first ? fileValue(first) : '',
        mapsTo: created[0]
          ? joinAxes(created[0].breakdown_value, created[0].sub_breakdown_value)
          : '',
        candidates: waiting?.candidates ?? [],
        targets,
        status,
        note,
      }
    })
}

export interface PreviewSummary {
  rowCount: number
  targetCount: number
  needsChoice: number
  leftOut: number
}

/** The file counted by its rows, as the table shows them; the backend's counts are per cell. */
export function previewSummary(labels: PreviewLabels, preview: FilePreview): PreviewSummary {
  const rows = previewRows(labels, preview)
  return {
    rowCount: preview.counts.rowCount || rows.length,
    targetCount: preview.counts.createdCount,
    needsChoice: rows.filter((row) => row.status === 'needs_choice').length,
    leftOut: rows.filter((row) => row.status === 'excluded').length,
  }
}

/** The strip's one-line subtitle: "12 rows · 30 targets · 2 need a choice". */
export function previewSubtitle(labels: PreviewLabels, preview: FilePreview): string {
  const { t } = labels
  const { rowCount, targetCount, needsChoice } = previewSummary(labels, preview)
  const parts = [t(`${K}.rows`, { count: rowCount }), t(`${K}.targets`, { count: targetCount })]
  if (needsChoice) {
    parts.push(t(needsChoice === 1 ? `${K}.needsOne` : `${K}.needsMany`, { count: needsChoice }))
  }
  return parts.join(' · ')
}

/** What picking a candidate sends: the choice goes back through the chat like any answer. */
export function candidateAnswer(row: PreviewRow, candidate: string): string {
  return `Map "${row.fileValue}" to "${candidate}"`
}

/** The dry run's own warnings, as sentences. */
export function previewWarnings(preview: FilePreview): string[] {
  return preview.noDataWarnings
    .map((warning) => {
      if (typeof warning === 'string') return warning
      const message = (warning as Record<string, unknown> | null)?.message
      return typeof message === 'string' ? message : ''
    })
    .filter(Boolean)
}
